import Papa from 'papaparse';
import { z } from 'zod';

export const dealSchema = z.object({
  athlete_name: z.string().min(1, "Athlete name is required"),
  athlete_email: z.string().email("Invalid email address").optional().or(z.literal("")),
  title: z.string().min(3, "Title must be at least 3 characters"),
  description: z.string().optional(),
  brand_name: z.string().min(2, "Brand name is required"),
  brand_contact_email: z.string().email("Invalid email address").optional().or(z.literal("")),
  brand_contact_phone: z.string().optional(),
  deal_type: z.enum(["endorsement", "appearance", "social_media", "autograph", "merchandise", "other"], {
    errorMap: () => ({ message: "Deal type must be: endorsement, appearance, social_media, autograph, merchandise, or other" })
  }),
  amount: z.coerce.number().min(0, "Amount must be positive"),
  start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Start date must be YYYY-MM-DD format"),
  end_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "End date must be YYYY-MM-DD format").optional().or(z.literal("")),
  deliverables: z.string().optional(),
  category: z.string().optional(),
});

export type DealRow = z.infer<typeof dealSchema>;

export interface ValidationResult {
  row: number;
  data: DealRow & { athlete_id?: string };
  status: 'success' | 'warning' | 'error';
  errors: string[];
}

export interface ParsedResult {
  valid: ValidationResult[];
  invalid: ValidationResult[];
  summary: {
    total: number;
    valid: number;
    invalid: number;
  };
}

interface Athlete {
  id: string;
  name: string;
  email: string | null;
}

const normalizeDealType = (type: string): string => {
  const normalized = type.toLowerCase().trim().replace(/\s+/g, '_');
  const typeMap: Record<string, string> = {
    'social_media': 'social_media',
    'social': 'social_media',
    'autograph_session': 'autograph',
    'autographs': 'autograph',
    'merch': 'merchandise',
  };
  return typeMap[normalized] || normalized;
};

const parseCurrency = (amount: string | number): number => {
  if (typeof amount === 'number') return amount;
  // Remove currency symbols, commas, and spaces
  const cleaned = amount.toString().replace(/[$,\s]/g, '');
  return parseFloat(cleaned) || 0;
};

const findAthleteByNameOrEmail = (
  nameOrEmail: string,
  athletes: Athlete[]
): Athlete | undefined => {
  const search = nameOrEmail.toLowerCase().trim();
  
  // First try exact email match
  const byEmail = athletes.find(a => a.email?.toLowerCase() === search);
  if (byEmail) return byEmail;
  
  // Try exact name match
  const byName = athletes.find(a => a.name.toLowerCase() === search);
  if (byName) return byName;
  
  // Try fuzzy name match (last name first format)
  const byReverseName = athletes.find(a => {
    const parts = a.name.split(' ');
    if (parts.length >= 2) {
      const reversed = `${parts[parts.length - 1]}, ${parts.slice(0, -1).join(' ')}`;
      return reversed.toLowerCase() === search;
    }
    return false;
  });
  
  return byReverseName;
};

export const parseCSV = (file: File): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.toLowerCase().trim().replace(/\s+/g, '_'),
      complete: (results) => {
        resolve(results.data);
      },
      error: (error) => {
        reject(error);
      },
    });
  });
};

export const validateDeals = (
  data: any[],
  athletes: Athlete[]
): ParsedResult => {
  const valid: ValidationResult[] = [];
  const invalid: ValidationResult[] = [];

  data.forEach((row, index) => {
    const errors: string[] = [];
    const rowNumber = index + 2; // +2 for header row and 0-indexing

    // Normalize deal type
    if (row.deal_type) {
      row.deal_type = normalizeDealType(row.deal_type);
    }

    // Parse amount if it's a string
    if (typeof row.amount === 'string') {
      row.amount = parseCurrency(row.amount);
    }

    // Find athlete by name or email
    let athlete: Athlete | undefined;
    if (row.athlete_name || row.athlete_email) {
      const searchTerm = row.athlete_email || row.athlete_name;
      athlete = findAthleteByNameOrEmail(searchTerm, athletes);
      
      if (!athlete) {
        errors.push(`Athlete not found: "${searchTerm}". Please add athlete first or check spelling.`);
      }
    } else {
      errors.push('Either athlete_name or athlete_email is required');
    }

    // Validate date range
    if (row.start_date && row.end_date) {
      const startDate = new Date(row.start_date);
      const endDate = new Date(row.end_date);
      if (endDate < startDate) {
        errors.push(`End date (${row.end_date}) must be after start date (${row.start_date})`);
      }
    }

    // Validate against schema
    const result = dealSchema.safeParse(row);
    
    if (result.success) {
      const dealData = {
        ...result.data,
        athlete_id: athlete?.id,
      };

      if (errors.length > 0) {
        invalid.push({
          row: rowNumber,
          data: dealData,
          status: 'error',
          errors,
        });
      } else {
        valid.push({
          row: rowNumber,
          data: dealData,
          status: 'success',
          errors: [],
        });
      }
    } else {
      const zodErrors = result.error.errors.map(err => 
        `${err.path.join('.')}: ${err.message}`
      );
      invalid.push({
        row: rowNumber,
        data: { ...row, athlete_id: athlete?.id },
        status: 'error',
        errors: [...errors, ...zodErrors],
      });
    }
  });

  return {
    valid,
    invalid,
    summary: {
      total: data.length,
      valid: valid.length,
      invalid: invalid.length,
    },
  };
};

export const generateCSVTemplate = (): string => {
  const headers = [
    'athlete_name',
    'athlete_email',
    'title',
    'brand_name',
    'deal_type',
    'amount',
    'start_date',
    'end_date',
    'description',
    'deliverables',
    'brand_contact_email',
    'brand_contact_phone',
    'category'
  ];
  
  const example = [
    'John Smith',
    'john.smith@university.edu',
    'Social Media Campaign - Nike Shoes',
    'Nike',
    'social_media',
    '1500',
    '2024-01-15',
    '2024-03-15',
    'Instagram posts featuring Nike products',
    '3 Instagram posts, 1 story per week',
    'marketing@nike.com',
    '555-0123',
    'Footwear'
  ];
  
  return `${headers.join(',')}\n${example.join(',')}`;
};

export const downloadTemplate = () => {
  const csv = generateCSVTemplate();
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'deals-import-template.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

export const downloadErrorReport = (invalid: ValidationResult[]) => {
  const headers = ['Row', 'Athlete', 'Brand', 'Title', 'Errors'];
  const rows = invalid.map(result => [
    result.row.toString(),
    result.data.athlete_name || 'N/A',
    result.data.brand_name || 'N/A',
    result.data.title || 'N/A',
    result.errors.join('; '),
  ]);
  
  const csv = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
  ].join('\n');
  
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'deal-import-errors.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};
